
import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Typography,
  Space,
  Button,
  message,
  Spin
} from 'antd';
import {
  SettingOutlined,
  UserOutlined,
  CreditCardOutlined,
  GlobalOutlined,
  CloudOutlined,
  TeamOutlined,
  SecurityScanOutlined
} from '@ant-design/icons';
import apiService from "services/api/apiService";
import EmailSettings from '../../components/Settings/EmailSettings';
import PaymentMethodSettings from '../../components/Settings/PaymentMethodSettings';
// Import sub-components (we'll create these)
import StorageManagement from '../../components/Settings/StorageManagement';
import SubscriptionBilling from '../../components/Settings/SubscriptionBilling';
import UserRoleManagement from '../../components/Settings/UserRoleManagement';
import LanguageCurrency from '../../components/Settings/LanguageCurrency';
import SystemConfiguration from '../../components/Settings/SystemConfiguration';
import PaymentIntegration from '../../components/Settings/PaymentIntegration';

const { Title } = Typography;
const { TabPane } = Tabs;

const SettingsAdministration: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('organization');
  const [settingsData, setSettingsData] = useState<any>({});

  const fetchSettingsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSettingsData();
      if (response.success) {
        setSettingsData(response.data);
      }
    } catch (error) {
      message.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettingsData();
  }, []);

  const tabItems = [
    {
      key: 'subscription',
      label: (
        <span>
          <CreditCardOutlined />
          Subscription & Billing
        </span>
      ),
      children: <SubscriptionBilling data={settingsData} onRefresh={fetchSettingsData} />
    },
    {
      key: 'users',
      label: (
        <span>
          <UserOutlined />
          User & Role Management
        </span>
      ),
      children: <UserRoleManagement data={settingsData} onRefresh={fetchSettingsData} />
    },
    {
      key: 'storage',
      label: (
        <span>
          <CloudOutlined />
          Storage Management
        </span>
      ),
      children: <StorageManagement />
    },
    {
      key: 'language',
      label: (
        <span>
          <GlobalOutlined />
          Language & Currency
        </span>
      ),
      children: <LanguageCurrency data={settingsData} onRefresh={fetchSettingsData} />
    },
    {
      key: 'payment',
      label: (
        <span>
          <CreditCardOutlined />
          Payment Integration
        </span>
      ),
      children: <PaymentIntegration data={settingsData} onRefresh={fetchSettingsData} />
    },
    {
      key: 'system',
      label: (
        <span>
          <SecurityScanOutlined />
          System Configuration
        </span>
      ),
      children: <SystemConfiguration data={settingsData} onRefresh={fetchSettingsData} />
    },
    {
      key: 'email',
      label: '📧 Email Settings',
      children: <EmailSettings data={settingsData} onRefresh={fetchSettingsData} />
    },
    {
      key: 'payment_method',
      label: '💳 Payment Method',
      children: <PaymentMethodSettings data={settingsData} onRefresh={fetchSettingsData} />
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0 }}>
            <SettingOutlined /> Settings & Administration
          </Title>
          <p style={{ color: '#666', margin: '8px 0 0 0' }}>
            Manage organizations, subscriptions, users, and system configuration
          </p>
        </div>

        <Spin spinning={loading}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="large"
            tabPosition="top"
          />
        </Spin>
      </Card>
    </div>
  );
};

export default SettingsAdministration;
